import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { LoadingState, PageHeader } from '../components/ui';
import accountService from '../services/accountService';
import accountLivenessService from '../services/accountLivenessService';
import groupService from '../services/groupService';
import projectService from '../services/projectService';
import api from '../services/api';
import {
  UsersIcon,
  WifiIcon,
  ServerIcon,
  Cog6ToothIcon,
  FolderIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import {
  ActiveProcessesPanel,
  AgentMessagesPanel,
  AttentionPanel,
  QuickNavigationPanel,
  StatTile,
  formatTimeAgo,
} from '../components/Dashboard/OperationsPanels';
import CommandCenterBar from '../components/Dashboard/CommandCenterBar';
import MessageVolumeMonitorPanel from '../components/Dashboard/MessageVolumeMonitorPanel';

const Dashboard = () => {
  const [focusMode, setFocusMode] = useState(false);
  const [summaryCopied, setSummaryCopied] = useState(false);
  const [refreshingNow, setRefreshingNow] = useState(false);
  const queryClient = useQueryClient();

  const { data: accounts = [], isLoading: accountsLoading, refetch: refetchAccounts } = useQuery(
    'accounts',
    accountService.getAccounts,
    { refetchInterval: 15000, keepPreviousData: true }
  );

  const { data: livenessData, refetch: refetchLiveness } = useQuery(
    'dashboard-liveness-status',
    accountLivenessService.getStatus,
    { refetchInterval: 15000, retry: 1 }
  );

  const { data: groupsCountData, refetch: refetchGroupsCount } = useQuery(
    'dashboard-groups-count',
    () => groupService.getGroupsCount({}),
    { refetchInterval: 60000, retry: 1 }
  );

  const { data: projects = [], refetch: refetchProjects } = useQuery(
    'dashboard-projects',
    projectService.getProjects,
    { refetchInterval: 30000, retry: 1 }
  );

  const { data: activeProcessesData, refetch: refetchActiveProcesses } = useQuery(
    'dashboard-active-processes',
    () => api.get('/processes/active').then((res) => res.data),
    { refetchInterval: 8000, retry: 1 }
  );

  const { data: processStatsData, refetch: refetchProcessStats } = useQuery(
    'dashboard-process-statistics',
    () => api.get('/processes/statistics', { params: { timeframe: '24h' } }).then((res) => res.data),
    { refetchInterval: 30000, retry: 1 }
  );

  const { data: agentMessagesData, refetch: refetchAgentMessages } = useQuery(
    'dashboard-agent-messages',
    () => api.get('/agents/messages', { params: { limit: 15 } }).then((res) => res.data),
    { refetchInterval: 10000, retry: 1 }
  );

  const forceLivenessSweepMutation = useMutation(() => accountLivenessService.forceCheckNow(), {
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries('dashboard-liveness-status'),
        queryClient.invalidateQueries('accounts'),
      ]);
    },
  });

  const activeProcesses = activeProcessesData?.processes || [];
  const processTotals = processStatsData?.statistics?.totals || {};
  const livenessSummary = livenessData?.summary || {};
  const agentMessages = agentMessagesData?.messages || [];

  const totalGroups = groupsCountData?.total || 0;
  const activeProjects = projects.filter((project) => project.is_active).length;
  const enabledAccounts = accounts.filter((account) => account.status === 'on').length;
  const onlineAccounts =
    typeof livenessSummary.online === 'number'
      ? livenessSummary.online
      : accounts.filter((account) => account.status === 'on' && account.live_status === 'online').length;
  const accountsWithSession = accounts.filter((account) => account.has_session).length;

  const accountsWithoutSession = accounts.filter(
    (account) => account.status === 'on' && !account.has_session
  ).length;
  const onlineIssues = accounts.filter(
    (account) => account.status === 'on' && account.live_status !== 'online'
  ).length;
  const accountsWithErrors = accounts.filter(
    (account) => account.status === 'on' && account.connection_error
  ).length;
  const failedProcesses24h = processTotals.failed_processes || 0;

  const attentionAccounts = useMemo(() => {
    const severityScore = (account) => {
      let score = 0;
      if (account.connection_error) score += 4;
      if (!account.has_session) score += 3;
      if (account.live_status === 'error') score += 3;
      if (account.live_status === 'offline') score += 2;
      if (account.live_status === 'unknown') score += 1;
      return score;
    };

    return accounts
      .filter((account) => {
        if (account.status !== 'on') return false;
        if (!account.has_session) return true;
        if (account.connection_error) return true;
        return account.live_status !== 'online';
      })
      .sort((a, b) => severityScore(b) - severityScore(a))
      .slice(0, 8);
  }, [accounts]);

  const attentionItems = [
    { id: 'session', label: 'Enabled accounts without session', value: accountsWithoutSession },
    { id: 'online', label: 'Enabled accounts not online', value: onlineIssues },
    { id: 'errors', label: 'Enabled accounts with connection errors', value: accountsWithErrors },
    { id: 'failed24h', label: 'Failed processes in last 24h', value: failedProcesses24h },
  ].filter((item) => item.value > 0);

  const latestProcessUpdatedAt = activeProcesses.reduce((latest, process) => {
    const candidate = process.updated_at || process.created_at;
    if (!candidate) return latest;
    if (!latest) return candidate;
    return new Date(candidate).getTime() > new Date(latest).getTime() ? candidate : latest;
  }, null);

  const latestAgentMessageAt = agentMessages[0]?.sent_at || null;

  const handleRefreshNow = async () => {
    setRefreshingNow(true);
    try {
      await Promise.all([
        refetchAccounts(),
        refetchLiveness(),
        refetchGroupsCount(),
        refetchProjects(),
        refetchActiveProcesses(),
        refetchProcessStats(),
        refetchAgentMessages(),
      ]);
    } finally {
      setRefreshingNow(false);
    }
  };

  const handleCopySummary = async () => {
    const summaryLines = [
      `TeleFarm Ops Snapshot - ${new Date().toLocaleString()}`,
      `Accounts enabled: ${enabledAccounts}/${accounts.length}`,
      `Online now: ${onlineAccounts}/${livenessSummary.total || accounts.length}`,
      `Sessions ready: ${accountsWithSession}/${accounts.length}`,
      `Active processes: ${activeProcesses.length}`,
      `Failed processes (24h): ${failedProcesses24h}`,
      `Enabled without session: ${accountsWithoutSession}`,
      `Enabled not online: ${onlineIssues}`,
      `Connection errors: ${accountsWithErrors}`,
    ];

    if (attentionAccounts.length > 0) {
      summaryLines.push(
        `Top impacted: ${attentionAccounts
          .slice(0, 3)
          .map((account) => `${account.name || account.phonenumber}(${account.live_status || 'unknown'})`)
          .join(', ')}`
      );
    }

    const text = summaryLines.join('\n');

    try {
      await navigator.clipboard.writeText(text);
      setSummaryCopied(true);
      setTimeout(() => setSummaryCopied(false), 2000);
    } catch (error) {
      window.prompt('Copy summary manually:', text);
    }
  };

  if (accountsLoading) {
    return <LoadingState message="Loading operations dashboard..." />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Operations Dashboard"
        description="Live operational view: account health, process flow, and agent activity"
      />

      <CommandCenterBar
        focusMode={focusMode}
        onToggleFocusMode={() => setFocusMode((value) => !value)}
        onRefreshNow={handleRefreshNow}
        refreshing={refreshingNow}
        onForceLivenessSweep={() => forceLivenessSweepMutation.mutate()}
        forceSweepLoading={forceLivenessSweepMutation.isLoading}
        onCopySummary={handleCopySummary}
        summaryCopied={summaryCopied}
        lastLivenessCheck={formatTimeAgo(livenessData?.lastCheck)}
        lastProcessUpdate={latestProcessUpdatedAt ? formatTimeAgo(latestProcessUpdatedAt) : 'unknown'}
        lastAgentMessage={latestAgentMessageAt ? formatTimeAgo(latestAgentMessageAt) : 'none yet'}
      />

      {!focusMode && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <StatTile
            title="Accounts Enabled"
            value={`${enabledAccounts}/${accounts.length}`}
            note={`${onlineAccounts} online now`}
            icon={UsersIcon}
            iconClass="bg-blue-600"
            to="/accounts"
          />
          <StatTile
            title="Runtime Liveness"
            value={`${onlineAccounts}/${livenessSummary.total || accounts.length}`}
            note={`Last check: ${formatTimeAgo(livenessData?.lastCheck)}`}
            icon={WifiIcon}
            iconClass="bg-green-600"
            to="/accounts"
          />
          <StatTile
            title="Sessions Ready"
            value={`${accountsWithSession}/${accounts.length}`}
            note={`${accountsWithoutSession} enabled without session`}
            icon={ServerIcon}
            iconClass="bg-indigo-600"
            to="/sessions"
          />
          <StatTile
            title="Active Processes"
            value={activeProcesses.length}
            note={`${failedProcesses24h} failed in 24h`}
            icon={Cog6ToothIcon}
            iconClass="bg-yellow-600"
            to="/processes"
          />
          <StatTile
            title="Projects"
            value={`${activeProjects}/${projects.length}`}
            note="active / total"
            icon={FolderIcon}
            iconClass="bg-purple-600"
            to="/projects"
          />
          <StatTile
            title="Groups"
            value={totalGroups}
            note="tracked in database"
            icon={UserGroupIcon}
            iconClass="bg-emerald-600"
            to="/groups"
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <AttentionPanel attentionItems={attentionItems} attentionAccounts={attentionAccounts} />
        <ActiveProcessesPanel processes={activeProcesses} />
      </div>

      <MessageVolumeMonitorPanel />

      {!focusMode && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <AgentMessagesPanel messages={agentMessages} />
          <QuickNavigationPanel />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
